import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  type?: "website" | "article";
  keywords?: string;
  structuredData?: object;
}

const SEOHead = ({
  title,
  description,
  canonicalPath = "",
  ogImage = "/ndf-logo.png",
  type = "website",
  keywords = "consultancy, data consultants, AI experts, tech consultants, expertise, Nederland, New Data Frontier",
  structuredData,
}: SEOHeadProps) => {
  const baseUrl = "https://newdatafrontier.nl";
  const fullUrl = `${baseUrl}${canonicalPath}`;
  const fullTitle = `${title} | New Data Frontier`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMeta = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        if (isProperty) {
          meta.setAttribute("property", name);
        } else {
          meta.setAttribute("name", name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Standard meta tags
    updateMeta("description", description);
    updateMeta("keywords", keywords);

    // Open Graph tags
    updateMeta("og:title", fullTitle, true);
    updateMeta("og:description", description, true);
    updateMeta("og:url", fullUrl, true);
    updateMeta("og:type", type, true);
    updateMeta("og:image", ogImage.startsWith("http") ? ogImage : `${baseUrl}${ogImage}`, true);
    updateMeta("og:site_name", "New Data Frontier", true);
    updateMeta("og:locale", "nl_NL", true);

    // Twitter Card tags
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", fullTitle);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", ogImage.startsWith("http") ? ogImage : `${baseUrl}${ogImage}`);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", fullUrl);

    // Structured data
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo="structured-data"]');
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "structured-data");
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup structured data on unmount
      const script = document.querySelector('script[data-seo="structured-data"]');
      if (script) {
        script.remove();
      }
    };
  }, [fullTitle, description, fullUrl, ogImage, type, keywords, structuredData]);

  return null;
};

export default SEOHead;
